"use client";

import React, { useEffect, useMemo, useRef, useState } from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { ButtonOutline } from "@/components/common/ButtonOutline";
import {
  createInitMessage,
  createSnapshotMessage,
  isPreviewProtocolMessage,
  isSelectSectionMessage,
  type PreviewSnapshot,
} from "./previewProtocol";
import type { Section } from "@/types/models/page";

type IframePreviewProps = {
  src: string;
  draftId: string;
  snapshot: PreviewSnapshot;
  reloadKey: number;
  onRefresh: () => void;
  onSectionSelect: (section: Section | null) => void;
};

export function IframePreview({
  src,
  draftId,
  snapshot,
  reloadKey,
  onRefresh,
  onSectionSelect,
}: IframePreviewProps) {
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const revisionRef = useRef(0);
  const [isReady, setIsReady] = useState(false);
  const [warning, setWarning] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const expectedOrigin = useMemo(() => {
    if (typeof window === "undefined") return "";
    return window.location.origin;
  }, []);

  useEffect(() => {
    if (isReady) return;

    const timeoutId = window.setTimeout(() => {
      setWarning("Preview loaded, but live sync has not connected yet.");
    }, 5000);

    return () => window.clearTimeout(timeoutId);
  }, [isReady, src, reloadKey]);

  useEffect(() => {
    setIsReady(false);
    setWarning(null);
    setError(null);
  }, [src, reloadKey]);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== expectedOrigin) return;
      if (event.source !== iframeRef.current?.contentWindow) return;
      if (!isPreviewProtocolMessage(event.data)) return;

      if (event.data.type === "READY") {
        setIsReady(true);
        setWarning(null);
        revisionRef.current += 1;
        iframeRef.current?.contentWindow?.postMessage(
          createInitMessage({
            draftId,
            snapshot,
            revision: revisionRef.current,
          }),
          expectedOrigin,
        );
        return;
      }

      if (event.data.type === "ERROR") {
        setError(event.data.message);
        return;
      }

      if (isSelectSectionMessage(event.data)) {
        const section = event.data.sectionId
          ? snapshot.sections.find((item) => item.id === event.data.sectionId) ||
            null
          : null;
        onSectionSelect(section);
        return;
      }

      if (
        event.data.type === "REORDER_SECTION" ||
        event.data.type === "UPDATE_SECTION"
      ) {
        if (import.meta.env.DEV) {
          console.warn("Unsupported iframe preview mutation ignored", event.data);
        }
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [draftId, expectedOrigin, onSectionSelect, snapshot]);

  useEffect(() => {
    if (!isReady) return;

    const timeoutId = window.setTimeout(() => {
      revisionRef.current += 1;
      iframeRef.current?.contentWindow?.postMessage(
        createSnapshotMessage({
          snapshot,
          revision: revisionRef.current,
        }),
        expectedOrigin,
      );
    }, 200);

    return () => window.clearTimeout(timeoutId);
  }, [expectedOrigin, isReady, snapshot]);

  return (
    <div className="relative h-full w-full overflow-hidden bg-white">
      <iframe
        key={`${draftId}-${reloadKey}`}
        ref={iframeRef}
        title="Webbuilder iframe preview"
        src={src}
        className="h-full w-full border-0 bg-white"
      />

      {(warning || error) && (
        <div className="absolute right-6 top-6 z-20 max-w-sm rounded-xl border border-amber-200 bg-white/95 p-3 text-sm shadow-lg backdrop-blur dark:border-amber-900 dark:bg-auth-card/95">
          <div className="flex items-start gap-2">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
            <div className="min-w-0 flex-1">
              <p className="font-medium text-foreground">
                {error ? "Preview failed" : "Preview warning"}
              </p>
              <p className="mt-1 text-muted-foreground">{error || warning}</p>
              <ButtonOutline
                onClick={onRefresh}
                className="mt-3 h-8 gap-2 px-3 text-xs"
              >
                <RefreshCw className="h-3.5 w-3.5" />
                Refresh
              </ButtonOutline>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
